import React, { useMemo } from "react";
import { Button } from "rsuite";
import Table from "../common/table/Table";
import PropTypes from "prop-types";
import greenBgTick from "../../assets/img/green-tick-filled.svg";
import greenTick from "../../assets/img/grey-tick-circle.svg";
import { useRoomsRateplansSelection } from "./hooks/useRoomsRateplansSelection";

const { Column, HeaderCell, Cell } = Table;

function RoomsRateplans({
  promo,
  rooms,
  ratePlans: rateplans,
  dataPropertyId = null,
  onSave,
  onSelectionChange,
}) {
  const {
    selectedCombo,
    selectedRateplan,
    selectedRooms,
    selectedRoomsAndRPs,
    mapping,
    getStatus,
    toggleCell,
    selectAllRoomsForRateplan,
    selectAllRPForRoom,
    selectAllRPAndRooms,
  } = useRoomsRateplansSelection({
    promo,
    rooms,
    ratePlans: rateplans,
    dataPropertyId,
    onSelectionChange,
  });

  // Memoize table data calculation
  const tableData = useMemo(() => {
    return rooms.map((room) => {
      const row = {
        room: room.displayName,
        roomId: room.id,
        roomCode: room.code,
      };
      for (const rateplan of rateplans) {
        row[rateplan.code] = {
          roomId: room.id,
          roomCode: room.code,
          rateCode: rateplan.code,
          rateplan: rateplan.name,
          rateplanId: rateplan.id,
          checked: getStatus(room.code, rateplan.code),
        };
      }
      return row;
    });
  }, [rooms, rateplans, getStatus]);

  // Calculate dynamic table height based on number of rows
  // Header: ~82px, Each row: ~46px, Scrollbar: ~14px
  const tableHeight = useMemo(() => {
    const headerHeight = 82;
    const rowHeight = 46;
    const scrollbarHeight = 14;
    const numberOfRows = tableData.length;
    const calculatedHeight =
      headerHeight + numberOfRows * rowHeight + scrollbarHeight;
    return calculatedHeight;
  }, [tableData.length]);

  return (
    <>
      <div className={"rooms-rateplans"} style={{ height: `${tableHeight}px` }}>
        <Table data={tableData}>
          <Column flexGrow={1} minWidth={200} align="left" fixed>
            <HeaderCell minWidth={200}>
              <div className="room-types-header">
                <span>Room Types</span>
              </div>
            </HeaderCell>
            <Cell>
              {(rowData) => (
                <button
                  type="button"
                  onClick={() => selectAllRPForRoom(rowData.roomCode)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      selectAllRPForRoom(rowData.roomCode);
                    }
                  }}
                  title={rowData.room}
                  className="room-type-cell"
                  aria-label={`Select all rate plans for ${rowData.roomCode}`}
                >
                  <span className="room-type-text">
                    {rowData.roomCode} | {rowData.room}
                  </span>
                </button>
              )}
            </Cell>
          </Column>
          <Column flexGrow={1} minWidth={100} align="left" fixed>
            <HeaderCell>
              <div className="rate-plans-header">
                <span>Rate Plans</span>
                <button
                  type="button"
                  className="cursor-pointer check-span check-span-header"
                  onClick={selectAllRPAndRooms}
                  aria-label="Select or deselect all rooms and rate plans"
                  aria-pressed={
                    selectedRoomsAndRPs.length ===
                    mapping.allRoomRateplans.length
                  }
                >
                  {selectedRoomsAndRPs.length ===
                  mapping.allRoomRateplans.length ? (
                    <img
                      src={greenBgTick}
                      alt="Selected"
                      className="check-icon-header"
                    />
                  ) : (
                    <img
                      src={greenTick}
                      alt="Unselected"
                      className="check-icon-header"
                    />
                  )}
                </button>
              </div>
            </HeaderCell>
            <Cell>
              {(rowData) => (
                <button
                  type="button"
                  className="cursor-pointer check-span check-span-row"
                  onClick={() => selectAllRPForRoom(rowData.roomCode)}
                  aria-label={`Select all rate plans for ${rowData.roomCode}`}
                >
                  {selectedRooms.includes(rowData.roomCode) ? (
                    <img
                      src={greenBgTick}
                      alt="Selected"
                      className="check-icon-row"
                    />
                  ) : (
                    <img
                      src={greenTick}
                      alt="Unselected"
                      className="check-icon-row"
                    />
                  )}
                </button>
              )}
            </Cell>
          </Column>
          {rateplans.map((rateplan) => {
            const minWidth = 100;
            return (
              <Column
                flexGrow={1}
                minWidth={minWidth}
                key={rateplan.id}
                align="center"
              >
                <HeaderCell>
                  <div className="rateplan-header">
                    <button
                      type="button"
                      title={rateplan.name}
                      className="rateplan-name"
                      onClick={() => selectAllRoomsForRateplan(rateplan.code)}
                      aria-label={`Select all rooms for ${rateplan.code}`}
                    >
                      {rateplan.code}
                    </button>
                    <button
                      type="button"
                      className="cursor-pointer check-span check-span-header"
                      onClick={() => {
                        selectAllRoomsForRateplan(rateplan.code);
                      }}
                      aria-label={`Select all rooms for ${rateplan.code}`}
                    >
                      {selectedRateplan.includes(rateplan.code) ? (
                        <img
                          src={greenBgTick}
                          alt="Selected"
                          className="check-icon-header"
                        />
                      ) : (
                        <img
                          src={greenTick}
                          alt="Unselected"
                          className="check-icon-header"
                        />
                      )}
                    </button>
                  </div>
                </HeaderCell>
                <Cell>
                  {(rowData) => {
                    const cellData = rowData?.[rateplan.code];
                    if (!cellData) {
                      return null;
                    }
                    return (
                      <button
                        type="button"
                        className="cursor-pointer check-span check-span-cell"
                        onClick={() => {
                          toggleCell(cellData);
                        }}
                        aria-label={`Toggle ${rowData.roomCode} and ${rateplan.code}`}
                        aria-pressed={cellData.checked}
                      >
                        {cellData.checked ? (
                          <img
                            src={greenBgTick}
                            alt="Selected"
                            className="check-icon-cell"
                          />
                        ) : (
                          <img
                            src={greenTick}
                            alt="Unselected"
                            className="check-icon-cell"
                          />
                        )}
                      </button>
                    );
                  }}
                </Cell>
              </Column>
            );
          })}
        </Table>
      </div>
      {onSave && (
        <div className="rooms-rateplans-save-button">
          <Button
            appearance="primary"
            onClick={() => onSave(selectedCombo)}
            className="save-btn-rooms-rateplans"
            disabled={!selectedCombo || selectedCombo.length === 0}
          >
            Save
          </Button>
        </div>
      )}
    </>
  );
}

RoomsRateplans.propTypes = {
  promo: PropTypes.shape({
    allowedRateRoomCodes: PropTypes.array,
  }),
  rooms: PropTypes.array.isRequired,
  ratePlans: PropTypes.array.isRequired,
  dataPropertyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSave: PropTypes.func,
  onSelectionChange: PropTypes.func,
};

export default RoomsRateplans;
